import React, { useState, useEffect, Fragment, useRef } from "react";
import { useMutation, useQuery } from "@apollo/client";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Select,
  MenuItem,
  TextField,
  InputAdornment,
  Stack,
  useTheme,
  useMediaQuery,
  Skeleton,
  IconButton,
  CircularProgress,
} from "@mui/material";
import {
  School as SchoolIcon,
  Search as SearchIcon,
  ArrowBack as ArrowBackIcon,
  Close as CloseIcon,
} from "@mui/icons-material";
import {
  GET_COURSE_SUBSCRIPTION,
  GET_COURSE_TEACHINGS,
  GET_USER_COURSES,
  GET_USER_HEADER,
} from "../../../../apollo-client/queries";
import { REMOVE_MY_COURSES } from "../../../../apollo-client/mutations";
import FuzzySearch from "fuzzy-search";
import MyCourse from "./my-course";
import { useIntl, FormattedMessage } from "react-intl";
import { useStore } from "../../../../store/store";
import { useIsMobile } from "../../../../hooks/state-checkers/use-is-mobile";

export default function MyCoursesNav({ toggle }) {
  const [universityList, setUniversityList] = useState([]);
  const selectedUniversity = useStore((state) => state.myCourseNavSelection);
  const [courseTitleFilter, setCourseTitleFilter] = useState("");

  const intl = useIntl();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const placeholderSearch = intl.formatMessage({
    id: "general.placeholder-search",
  });
  const myCoursesHeadline = intl.formatMessage({ id: "courses-nav.title" });

  const ref = useRef(null);
  const { menuIsOpen } = useStore();

  // Custom outside click handler that doesn't close when interacting with Select dropdowns
  useEffect(() => {
    function handleClickOutside(event) {
      // Don't trigger outside click if a menu is open
      if (menuIsOpen) {
        return;
      }
      
      // Don't close if clicking on Select dropdown elements
      const target = event.target;
      const isSelectElement = target.closest('.MuiSelect-root') || 
                             target.closest('.MuiMenu-root') || 
                             target.closest('.MuiPaper-root') ||
                             target.closest('[role="presentation"]') ||
                             target.closest('[role="menu"]') ||
                             target.closest('[role="option"]');
      
      if (ref.current && !ref.current.contains(event.target) && !isSelectElement) {
        toggle();
      }
    }

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [menuIsOpen, toggle]);

  const { data: userCoursesData, loading, error } = useQuery(GET_USER_COURSES);

  const [removeMyCourses] = useMutation(REMOVE_MY_COURSES, {
    refetchQueries: [
      GET_COURSE_SUBSCRIPTION,
      GET_USER_HEADER,
      // for refetching subsriber count
      GET_COURSE_TEACHINGS,
      GET_USER_COURSES,
    ],
    awaitRefetchQueries: true,
  });

  const fillUnisToDropdown = () => {
    let universityArray = [];
    userCoursesData.user.universities.forEach((university) =>
      universityArray.push(university.name)
    );
    userCoursesData.user.courses.courses.forEach((course) => {
      if (!universityArray.includes(course?.university)) {
        universityArray.push(course?.university);
      }
    });
    setUniversityList(universityArray);
  };

  const SetUniCourses = (university) => {
    console.log('SetUniCourses called with:', university);
    console.log('Current selectedUniversity:', selectedUniversity);
    
    if (!userCoursesData || !userCoursesData.user || !userCoursesData.user.courses) {
      console.log('No userCoursesData available');
      return;
    }

    let selectedUniversityCourses = [];
    userCoursesData.user.courses.courses
      .filter((course) => course?.university === university)
      .forEach((course) => selectedUniversityCourses.push(course));

    console.log('Filtered courses for university:', selectedUniversityCourses);

    useStore.setState({
      myCourseNavSelection: {
        university: university,
        courses: selectedUniversityCourses,
      },
    });
    
    console.log('State updated with university:', university);
  };

  useEffect(() => {
    if (userCoursesData && userCoursesData.user.universities[0]) {
      // Only set initial university if no university is currently selected
      if (!selectedUniversity.university || selectedUniversity.university === "") {
        SetUniCourses(userCoursesData.user.universities[0].name);
      } else {
        // Update the courses for the currently selected university when data changes
        SetUniCourses(selectedUniversity.university);
      }
      fillUnisToDropdown();
    }
  }, [userCoursesData, selectedUniversity.university]);

  // Debug logging
  console.log('Current selectedUniversity state:', selectedUniversity);
  console.log('University list:', universityList);
  console.log('userCoursesData:', userCoursesData?.user?.courses?.courses?.length, 'courses');

  let searcher;
  if (userCoursesData !== "" && selectedUniversity?.courses)
    searcher = new FuzzySearch(selectedUniversity.courses, ["title"]);

  return (
    <Box
      id={"my_courses_nav_container"}
      data-testid="my-courses-nav"
      ref={ref}
      sx={{
        position: 'fixed',
        bottom: 0,
        left: 0,
        display: 'flex',
        flexDirection: 'column',
        width: { xs: '100%', sm: '42%' },
        minWidth: { xs: '100%', sm: 530 },
        maxWidth: { xs: '100%', sm: 640 },
        height: '100%',
        backgroundColor: 'background.paper',
        boxShadow: '0 -4px 12px rgba(0, 0, 0, 0.15)',
        zIndex: 13,
        overflow: 'hidden',
      }}
    >
      {/* Header */}
      <Card sx={{ 
        borderRadius: 0,
        boxShadow: 'none',
        borderBottom: '1px solid',
        borderColor: 'divider'
      }}>
        <CardContent sx={{ 
          p: { xs: 2, sm: 3 },
          '&:last-child': { pb: { xs: 2, sm: 3 } }
        }}>
          <Stack 
            direction="row" 
            alignItems="center" 
            justifyContent="space-between"
            spacing={2}
          >
            {/* Mobile back button */}
            {isMobile && (
              <IconButton 
                onClick={toggle}
                size="small"
                sx={{ 
                  color: 'text.secondary',
                  '&:hover': { backgroundColor: 'action.hover' }
                }}
              >
                <ArrowBackIcon />
              </IconButton>
            )}

            {/* Title */}
            <Stack direction="row" alignItems="center" spacing={1.5}>
              <SchoolIcon 
                sx={{ 
                  fontSize: { xs: 24, sm: 28 },
                  color: 'primary.main'
                }} 
              />
              <Typography 
                variant="h5" 
                fontWeight="bold"
                sx={{ 
                  fontSize: { xs: '1.25rem', sm: '1.5rem' },
                  color: 'text.primary'
                }}
              >
                <FormattedMessage id="courses-nav.title" />
              </Typography>
            </Stack>

            {/* Desktop close button */}
            {!isMobile && (
              <IconButton 
                onClick={toggle}
                size="small"
                sx={{ 
                  color: 'text.secondary',
                  '&:hover': { backgroundColor: 'action.hover' }
                }}
              >
                <CloseIcon />
              </IconButton>
            )}
          </Stack>
        </CardContent>
      </Card>

      {/* Content */}
      <Box sx={{ 
        flex: 1, 
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column'
      }}>
        {loading ? (
          <Box sx={{ p: { xs: 2, sm: 3 } }}>
            <Skeleton 
              variant="rectangular" 
              height={56} 
              sx={{ 
                borderRadius: 1, 
                mb: 2 
              }} 
            />
            <Skeleton 
              variant="rectangular" 
              height={400} 
              sx={{ borderRadius: 1 }} 
            />
          </Box>
        ) : (
          <Fragment>
            {/* University Selection and Search */}
            <Card sx={{ 
              m: { xs: 2, sm: 3 },
              mb: 2,
              boxShadow: 1
            }}>
              <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
                <Stack spacing={2}>
                  {/* University Selection */}
                  <Box>
                    <Typography 
                      variant="subtitle2" 
                      color="text.secondary" 
                      gutterBottom
                      sx={{ mb: 1 }}
                    >
                      <FormattedMessage id="courses-nav.university" defaultMessage="University" />
                    </Typography>
                    <Select
                      value={selectedUniversity?.university || ''}
                      onChange={(e) => {
                        console.log('University selected:', e.target.value);
                        SetUniCourses(e.target.value);
                      }}
                      fullWidth
                      variant="outlined"
                      size="small"
                      startAdornment={<SchoolIcon sx={{ mr: 1, color: 'text.secondary' }} />}
                      sx={{
                        '& .MuiOutlinedInput-notchedOutline': {
                          borderColor: 'rgba(0,0,0,0.23)',
                        },
                        '&:hover .MuiOutlinedInput-notchedOutline': {
                          borderColor: 'primary.main',
                        },
                      }}
                    >
                      {universityList.map((university) => (
                        <MenuItem key={university} value={university}>
                          <Typography 
                            variant="body2" 
                            sx={{ 
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                              maxWidth: '100%'
                            }}
                          >
                            {university}
                          </Typography>
                        </MenuItem>
                      ))}
                    </Select>
                  </Box>

                  {/* Search */}
                  <Box>
                    <Typography 
                      variant="subtitle2" 
                      color="text.secondary" 
                      gutterBottom
                      sx={{ mb: 1 }}
                    >
                      <FormattedMessage id="courses-nav.search" defaultMessage="Search Courses" />
                    </Typography>
                    <TextField
                      fullWidth
                      size="small"
                      placeholder={placeholderSearch}
                      value={courseTitleFilter}
                      onChange={(e) => setCourseTitleFilter(e.target.value)}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <SearchIcon sx={{ color: 'text.secondary' }} />
                          </InputAdornment>
                        ),
                      }}
                      sx={{
                        '& .MuiOutlinedInput-notchedOutline': {
                          borderColor: 'rgba(0,0,0,0.23)',
                        },
                        '&:hover .MuiOutlinedInput-notchedOutline': {
                          borderColor: 'primary.main',
                        },
                      }}
                    />
                  </Box>
                </Stack>
              </CardContent>
            </Card>

            {/* Courses List */}
            <Box 
              data-testid="my-courses-list"
              sx={{ 
                flex: 1,
                overflow: 'auto',
                px: { xs: 2, sm: 3 },
                pb: 2
              }}
            >
              {!loading &&
                !error &&
                userCoursesData &&
                searcher && (
                  <Stack spacing={1}>
                    {searcher.search(courseTitleFilter).map((course, i) => (
                      <MyCourse
                        key={"my_course_" + i}
                        courseId={course._id}
                        course={course}
                        userId={userCoursesData.user._id}
                        userCourses={userCoursesData.user.courses.courses}
                        isCourseNotAdded={course.isCourseNotAdded}
                        i={i}
                        removeMyCourses={removeMyCourses}
                        closeCourseNav={toggle}
                      />
                    ))}
                  </Stack>
                )}
            </Box>
          </Fragment>
        )}
      </Box>
    </Box>
  );
}

